import { Outlet, useOutletContext, useLocation } from "react-router-dom";
import React, { useState, useCallback } from "react";
import { makeStyles, Combobox, Option } from "@fluentui/react-components";
import { List, MapPin, MagnifyingGlass } from "@phosphor-icons/react";

import FeatureTour from "../../components/FeatureTour/FeatureTour";
import ToUPopup from "../../components/ToUPopup/ToUPopup";
import { useCookies } from "react-cookie";
import NavigationBar from "./NavigationBar";
import { useDomainSelection } from "../../hooks/useDomainSelection";
import { useAppSelector } from "../../store";
import { selectDomainRouting, selectDomainConfig } from "../../store/selectors/userSelectors";

// Import theme tokens
import { tokens } from "../../theme";
import { brand } from "@brand";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Colors
    themePrimary: tokens.palette["theme-Primary"],
    neutralInverse: tokens.semantic.neutralInverse,
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],
    semanticTransparent: tokens.palette.semanticTransparent,

    // Typography
    fontFamilyHeader: tokens.primitives["font-Family-Header"],
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase100: tokens.primitives["font-Size-Base-100"],
    fontSizeBase200: tokens.primitives["font-Size-Base-200"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightLight: tokens.primitives["font-Weight-Light"],
    fontWeightSemiBold: tokens.primitives["font-Weight-SemiBold"],
    lineHeightHero700: tokens.primitives["lineHeight-Hero-700"],

    // Spacing - Vertical
    spacingVerticalNone: tokens.primitives["spacing-Vertical-None"],
    spacingVerticalXxs: tokens.primitives["spacing-Vertical-XXS"],
    spacingVerticalXs: tokens.primitives["spacing-Vertical-XS"],
    spacingVerticalL: tokens.primitives["spacing-Vertical-L"],
    spacingVerticalXl: tokens.primitives["spacing-Vertical-XL"],
    spacingVerticalXxl: tokens.primitives["spacing-Vertical-XXL"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],

    // Spacing - Horizontal
    spacingHorizontalNone: tokens.primitives["spacing-Horizontal-None"],
    spacingHorizontalS: tokens.primitives["spacing-Horizontal-S"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalMNudge: tokens.primitives["spacing-Horizontal-MNudge"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],

    // Radius
    radiusMedium: tokens.primitives["stroke-Radius-Medium"],
    radiusXLarge: tokens.primitives["stroke-Radius-XLarge"],

    // Shadows
    shadowRestingSmall: tokens.shadows["shadow-Resting-Small"]
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    layout: {
        display: "flex",
        flexDirection: "row",
        height: "100%",
        overflowX: "hidden"
    },

    sidebar: {},

    mainContentLayout: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        height: "100%",
        minWidth: "300px",
        overflow: "hidden" // Add this to prevent the whole layout from scrolling
    },

    // Add this new style
    mainContent: {
        flexGrow: 1,
        overflowY: "auto",
        overflowX: "hidden"
    },

    header: {
        backgroundColor: t.themePrimary,
        flexShrink: 0,
        height: "64px"
    },

    taglineSection: {
        width: "100%",
        textAlign: "center" as const,
        color: t.neutralInverse,
        fontFamily: t.fontFamilyHeader,
        fontSize: t.lineHeightHero700,
        lineHeight: t.lineHeightHero700,
        fontWeight: t.fontWeightSemiBold as unknown as number,
        padding: t.spacingVerticalXl,
        backgroundColor: t.themePrimary,

        "@media only screen and (max-width: 480px)": {
            backgroundColor: t.semanticTransparent,
            color: t.themePrimary,
            fontSize: t.fontSizeBase400,
            padding: `${t.spacingVerticalXxl} ${t.spacingHorizontalL}`
        }
    },

    headerContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        height: "100%",
        padding: `0 ${t.spacingHorizontalL}`,

        "@media only screen and (max-width: 480px)": {
            padding: `0 ${t.spacingHorizontalM}`
        },

        "& a": {
            textDecoration: "none",
            color: t.neutralInverse
        }
    },

    leftHeader: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: t.spacingHorizontalS,
        height: "100%"
    },

    centerHeader: {
        flexGrow: 1,
        flexShrink: 3
    },

    rightHeader: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        position: "relative",
        gap: t.spacingHorizontalS,
    },

    domainPicker: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        backgroundColor: "rgba(255,255,255,0.9)",
        borderRadius: "8px",
        padding: "4px 8px",
        maxWidth: "220px",
        overflow: "hidden",

        "@media screen and (max-width: 480px)": {
            maxWidth: "160px",
            padding: "3px 6px",
        },
    },

    domainIcon: {
        color: t.themePrimary,
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
    },

    domainDropdown: {
        minWidth: "100px",
        flex: 1,
        overflow: "hidden",
        backgroundColor: "transparent",
        "& input": {
            backgroundColor: "transparent !important",
            border: "none !important",
            color: brand.colors.domainPickerText,
            fontSize: "14px",
            fontWeight: 500,
            padding: "0 4px",
            minHeight: "28px",
            width: "100%",
            textOverflow: "ellipsis",
        },
        "& button": {
            backgroundColor: "transparent !important",
        },
        "& div": {
            backgroundColor: "transparent !important",
            border: "none !important",
        },
        "& div::after": {
            display: "none !important",
        },

        "@media screen and (max-width: 480px)": {
            minWidth: "80px",
            "& input": {
                fontSize: "13px",
            },
        },
    },

    menuButton: {
        padding: t.spacingVerticalL,
        cursor: "pointer",
        borderRadius: t.radiusMedium,
        border: "none",
        background: "none",
        color: t.neutralInverse,
        fontSize: t.fontSizeBase400,
        marginTop: t.spacingVerticalXxs,

        "@media only screen and (max-width: 600px)": {
            fontSize: t.fontSizeBase300
        },

        "@media (pointer: coarse)": {
            fontSize: t.fontSizeBase300
        },

        "@media only screen and (max-width: 375px)": {
            fontSize: t.fontSizeBase200
        }
    },

    iconItem: {
        "& img": {
            filter: "brightness(0) saturate(100%) invert(100%) sepia(7%)",
            width: "215px",
            height: "70px"
        },

        "@media only screen and (max-width: 600px)": {
            "& img": {
                width: "160px",
                height: "auto"
            }
        }
    },

    imgContainer: {
        width: "215px",
        height: "70px",

        "& img": {
            minWidth: "100%"
        },

        "@media only screen and (max-width: 600px)": {
            width: "160px",
            height: "auto"
        }
    },

    homeButtonContainer: {
        display: "flex",
        flexDirection: "row",
        gap: t.spacingHorizontalMNudge
    },

    mainTextItem: {
        marginTop: "auto",
        lineHeight: "50px",
        marginBottom: "auto",
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightSemiBold as unknown as number,

        "@media only screen and (max-width: 600px)": {
            fontSize: t.fontSizeBase300
        },

        "@media (pointer: coarse)": {
            fontSize: t.fontSizeBase300
        },

        "@media only screen and (max-width: 375px)": {
            fontSize: t.fontSizeBase300
        }
    },

    stageLabel: {
        marginTop: "auto",
        marginBottom: "auto",
        padding: t.spacingVerticalXs,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightLight as unknown as number,
        marginLeft: t.spacingHorizontalL,

        "@media only screen and (max-width: 600px)": {
            display: "none"
        },

        "@media (pointer: coarse)": {
            fontSize: t.fontSizeBase200,
            marginLeft: t.spacingHorizontalNone
        },

        "@media only screen and (max-width: 375px)": {
            fontSize: t.fontSizeBase100,
            padding: t.spacingVerticalNone
        }
    },

    locationIcon: {
        fontSize: t.fontSizeBase400,
        position: "absolute" as const,
        paddingLeft: t.spacingHorizontalL,

        "@media only screen and (max-width: 375px)": {
            fontSize: t.fontSizeBase200
        }
    },

    locationDropdown: {
        padding: t.spacingVerticalXs,
        marginTop: "auto",
        marginBottom: "auto"
    },

    selectMenu: {
        cursor: "pointer",
        background: t.neutralLighterAlt,
        color: t.themePrimary,
        borderRadius: t.radiusXLarge,
        padding: t.spacingVerticalXs,
        textIndent: "35px",
        border: "none",
        boxShadow: t.shadowRestingSmall,
        width: "200px",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontFamily: t.fontFamilyBase,

        "@media only screen and (max-width: 600px)": {
            width: "180px"
        },

        "@media only screen and (max-width: 375px)": {
            fontSize: t.fontSizeBase200,
            width: "150px"
        }
    },

    messageBar: {
        position: "sticky" as const,
        top: "75px",
        zIndex: 1,
        marginBottom: t.spacingVerticalM
    },

    mobileMenuButton: {
        display: "none",
        cursor: "pointer",
        background: "none",
        border: "none",
        padding: t.spacingVerticalXs,
        color: t.neutralInverse,

        "@media screen and (max-width: 480px)": {
            display: "flex",
            alignItems: "center"
        }
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

const Layout = () => {
    const styles = useStyles();
    const location = useLocation();
    const [chatStarted, setChatStarted] = useState(false);
    const [, setClearChatCallback] = useState<() => void>(() => {});
    const [isNavOpen, setIsNavOpen] = useState(false);
    const isHomePage = location.pathname === "/";
    const domainRouting = useAppSelector(selectDomainRouting);
    const domainConfig = useAppSelector(selectDomainConfig);
    const { domains, selectedSlug, setSelectedSlug, shouldShowSelector, effectiveDomainSlug } = useDomainSelection({
        allowedDomainSlugs: domainRouting?.allowedDomainSlugs,
        defaultDomainSlug: domainRouting?.defaultDomainSlug,
        locked: domainRouting?.locked,
    });
    const [rawCookies, setCookie] = useCookies(["accepted", "visited"]);
    const cookies = rawCookies as { accepted?: string; visited?: string };

    // Domain searchable dropdown: manual filtering for "contains" matching.
    // When nothing is selected we surface a prompt rather than an "all" option —
    // multi-base behavior is no longer supported in user-facing channels;
    // every conversation must be scoped to a single base.
    const selectedDomainName = selectedSlug ? domains.find((d) => d.slug === selectedSlug)?.name ?? `Select a ${brand.domainNoun.singular}` : `Select a ${brand.domainNoun.singular}`;
    const [domainSearch, setDomainSearch] = useState("");
    const [domainFocused, setDomainFocused] = useState(false);

    const filteredDomains = domainSearch
        ? domains.filter((d) => d.name.toLowerCase().includes(domainSearch.toLowerCase()))
        : domains;

    const handleDomainSelect = useCallback((_e: unknown, data: { optionValue?: string }) => {
        setSelectedSlug(data.optionValue === "" ? undefined : data.optionValue);
        setDomainSearch("");
        setDomainFocused(false);
    }, [setSelectedSlug]);

    const handleUserAcceptedTerms = () => {
        setCookie("accepted", "true", { path: "/" });
    };

    const handleSetClearChatCallback = (newCallback: () => void) => {
        setClearChatCallback(() => newCallback);
    };

    const onTourEnd = () => {
        const cookie = document.cookie;
        if (cookie.indexOf("visited=true") === -1) {
            const date = new Date();
            date.setTime(date.getTime() + 400 * 24 * 60 * 60 * 1000);
            document.cookie = "visited=true; expires=" + date.toUTCString() + "; path=/";
        }
    };

    const toggleNavBar = () => {
        setIsNavOpen(prev => !prev);
    };

    return (
        <div className={styles.layout}>
            {/* Skip link for keyboard users */}
            <a
                href="#main-content"
                style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden"
                }}
                onFocus={e => {
                    e.currentTarget.style.left = "10px";
                    e.currentTarget.style.top = "10px";
                    e.currentTarget.style.width = "auto";
                    e.currentTarget.style.height = "auto";
                    e.currentTarget.style.overflow = "visible";
                    e.currentTarget.style.zIndex = "9999";
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.padding = "10px";
                    e.currentTarget.style.border = "2px solid #005a9e";
                }}
                onBlur={e => {
                    e.currentTarget.style.left = "-10000px";
                    e.currentTarget.style.width = "1px";
                    e.currentTarget.style.height = "1px";
                    e.currentTarget.style.overflow = "hidden";
                }}
            >
                Skip to main content
            </a>
            <div className={styles.sidebar}>
                <NavigationBar isMenuOpen={isNavOpen} toggleNav={toggleNavBar} />
            </div>
            <div className={styles.mainContentLayout}>
                <header className={styles.header} role={"banner"}>
                    <div className={styles.headerContainer}>
                        <div className={styles.leftHeader}>
                            <button className={styles.mobileMenuButton} onClick={toggleNavBar} aria-label="Toggle navigation menu" aria-expanded={isNavOpen}>
                                <List size={28} weight="bold" aria-hidden="true" />
                            </button>
                            <div className={styles.iconItem}>
                                <a href={"/"} aria-label={`${brand.name} - Go to home page`}>
                                    <div className={styles.homeButtonContainer}>
                                        <div className={styles.imgContainer}>
                                            <img src={brand.logo} alt="" />
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                        <div className={styles.centerHeader}></div>
                        {shouldShowSelector && (
                            <div className={styles.rightHeader}>
                                <div className={styles.domainPicker}>
                                    <span className={styles.domainIcon}>
                                        {domainFocused
                                            ? <MagnifyingGlass size={16} weight="bold" aria-hidden="true" />
                                            : <MapPin size={16} weight="fill" aria-hidden="true" />
                                        }
                                    </span>
                                    <Combobox
                                        className={styles.domainDropdown}
                                        appearance="underline"
                                        size="small"
                                        style={{ borderBottom: "none" }}
                                        value={domainFocused ? domainSearch : selectedDomainName}
                                        selectedOptions={domainFocused ? [] : (selectedSlug ? [selectedSlug] : [])}
                                        onOptionSelect={handleDomainSelect}
                                        onChange={(e) => setDomainSearch(e.target.value)}
                                        onFocus={() => { setDomainFocused(true); setDomainSearch(""); }}
                                        onBlur={() => { setTimeout(() => { setDomainFocused(false); setDomainSearch(""); }, 150); }}
                                        aria-label={`Search and select ${brand.domainNoun.singular}`}
                                        placeholder={`Type to filter ${brand.domainNoun.plural}...`}
                                        positioning={{ position: "below", align: "end" }}
                                        freeform
                                    >
                                        {/* Clear option — only shown when a base is
                                            selected and there's no active filter text
                                            (so the user always sees it as the first
                                            option in the unfiltered list). Hits
                                            handleDomainSelect's empty-value branch
                                            which calls setSelectedSlug(undefined). */}
                                        {selectedSlug && !domainSearch && (
                                            <Option key="__clear" value="" text="Clear selection">
                                                Clear selection
                                            </Option>
                                        )}
                                        {filteredDomains.map((d) => (
                                            <Option key={d.id} value={d.slug}>
                                                {d.name}
                                            </Option>
                                        ))}
                                    </Combobox>
                                </div>
                            </div>
                        )}
                    </div>
                </header>
                {/* Only show tagline when chat hasn't started */}
                {isHomePage && !chatStarted && (
                    <div className={styles.taglineSection} role="heading" aria-level={1}>
                        {brand.tagline}
                    </div>
                )}

                {!cookies.accepted && <ToUPopup handleUserContinue={handleUserAcceptedTerms} />}
                <FeatureTour isTermsAccepted={Boolean(cookies.accepted)} isTourCompleted={Boolean(cookies.visited)} onTourEnd={onTourEnd}></FeatureTour>

                {/* Wrap Outlet in scrollable container */}
                <main id="main-content" className={styles.mainContent}>
                    <Outlet context={{ setChatStarted, setClearChatCallback: handleSetClearChatCallback, effectiveDomainSlug, domainConfig, setSelectedSlug }} />
                </main>
            </div>
        </div>
    );
};

export default Layout;

type ChatContextType = {
    setChatStarted: React.Dispatch<React.SetStateAction<boolean>>;
    setClearChatCallback: React.Dispatch<React.SetStateAction<() => void>>;
    effectiveDomainSlug: string | undefined;
    domainConfig?: { mode: "soft" | "hard"; selectionPrompt?: string; domainLabel?: string } | null;
    /** Set the current base slug — used by Chat.tsx when the backend
     *  auto-resolves the base from a user's message and echoes it back. */
    setSelectedSlug: (slug: string | undefined) => void;
};
export function useChatContext() {
    return useOutletContext<ChatContextType>();
}
